import { DEFAULT_CATEGORY_ICON, DEFAULT_ICON_SIZE, ICONS8_KEY } from '../helpers/constants';
import { LitElement, css, html } from 'lit';
import { NumberedIconShape, SearchIcon } from '../types';
import { customElement, property, query, state } from 'lit/decorators';
import { debounce, isDefined, undefinedIfEmpty } from '../helpers/utils';
import { dupeCategoriesExist, validateIcon } from '../helpers/tags-categories';
import { externalGet, logWithAmplitude, throwHttpError } from '../helpers/api/api';
import { getColorFromCircleIcon, getThemeColors, isGeneratedColorIcon, makeColorIntoDataSvg } from '../helpers/colors';

import { RangeSliderComponent } from './range-slider-component';
import { StateController } from '../helpers/state/state-management/state-controller';
import { TextField } from '@material/mwc-textfield';
import { getMainPage } from '../helpers/page-name';
import topicData from '../helpers/state/topic-data';

const numberedIconShapeOptions: Array<{ value: NumberedIconShape; label: string }> = [
    { value: NumberedIconShape.Circle, label: 'Circle' },
    { value: NumberedIconShape.Square, label: 'Square' },
    { value: NumberedIconShape.Star, label: 'Star' },
    { value: NumberedIconShape.Diamond, label: 'Diamond' },
    { value: NumberedIconShape.Hexagon, label: 'Hexagon' },
];

@customElement('category-form')
export class CategoryForm extends LitElement {
    amplitude_callsite: string = 'Category Form';

    @query('#colorPicker')
    private readonly colorPickerElement!: HTMLInputElement;

    @query('#search')
    search!: TextField;

    @property({ type: Object })
    pickedIcon?: SearchIcon;

    @state()
    selectedIconUrl: string | undefined;

    @property({ type: String })
    categoryID: string | undefined;

    @property({ type: Boolean })
    creatingNewCategory = false;

    @state()
    rotatePreviewCSS: string = '';

    @state()
    tabIndex: number = 0;

    @state()
    suggestedColors: string[] = [];

    @state()
    pickedColor: string = '';

    @state()
    iconSearchResults: SearchIcon[] = [];

    @state()
    awaitingImageUpload = false;

    @state()
    customIconSize: boolean = false;

    private readonly bindTopicState = new StateController(this, topicData);

    firstUpdated() {
        this.setupPalette();
        if (isDefined(this.category?.icon_size) && this.category?.icon_size !== topicData.topic?.icon_size) {
            this.customIconSize = true;
        }
    }

    get category() {
        return topicData.categories.find((category) => category._id?.$oid === this.categoryID);
    }

    get categoryIcon() {
        return topicData.categories.find((category) => category._id?.$oid === this.categoryID)?.icon ?? DEFAULT_CATEGORY_ICON;
    }

    get renderableIconString() {
        const markerShape = this.category?.marker_shape ?? topicData.topic?.marker_shape ?? NumberedIconShape.Circle;

        if (this.categoryIcon.startsWith('#')) {
            return makeColorIntoDataSvg(this.categoryIcon, markerShape);
        }

        if (isGeneratedColorIcon(this.categoryIcon)) {
            return makeColorIntoDataSvg(getColorFromCircleIcon(this.categoryIcon) ?? '#102C3D', markerShape);
        }

        return this.categoryIcon;
    }

    async onChangeCategoryName(e: CustomEvent) {
        const name = (e.target as HTMLInputElement).value.trim();
        const dupeCategoryName = dupeCategoriesExist(topicData.categories, name);

        if (dupeCategoryName) {
            getMainPage().errorToastElement.showToastAndThrow(`A category named ${dupeCategoryName} already exists. Please choose a different name.`);
        }

        if (this.category?._id) {
            await topicData.fetchAndUpdateCategory(
                this.category?._id.$oid,
                {
                    name
                },
                'category editor'
            );
        }
    }

    async onChangeCategoryDescription(e: CustomEvent) {
        const description = (e.target as HTMLInputElement).value;

        if (this.category?._id) {
            await topicData.fetchAndUpdateCategory(
                this.category?._id.$oid,
                {
                    description
                },
                'category editor'
            );
        }
    }

    async pickIconColor(iconColor: string) {
        this.suggestedColors = [...new Set([iconColor, ...this.suggestedColors])];
        await this.onChangeCategoryIconColor(iconColor);
        await this.requestUpdate();
    }

    async onChangeCategoryIconColor(iconColor: string) {
        this.pickedColor = iconColor;
        const validatedIcon = await validateIcon(iconColor);

        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }

        this.cancelImageUpload();

        if (this.category?._id) {
            await topicData.fetchAndUpdateCategory(
                this.category?._id.$oid,
                {
                    icon: validatedIcon
                },
                'category editor'
            );
        }
    }

    private async customIconSelected(iconUrl: string) {
        const validatedIcon = await validateIcon(iconUrl);
        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }

        this.cancelImageUpload();

        if (this.category?._id) {
            await topicData.fetchAndUpdateCategory(
                this.category?._id.$oid,
                {
                    icon: validatedIcon
                },
                'category editor'
            );
        }
    }

    private openColorPicker() {
        this.colorPickerElement.focus();
        this.colorPickerElement.value = this.categoryIcon ?? '';
        this.colorPickerElement.click();

        logWithAmplitude(this.amplitude_callsite, 'Open Color Picker');
    }

    cancelImageUpload() {
        this.rotatePreviewCSS = '';
        this.dispatchEvent(new CustomEvent('on-change-category-image', {
            detail: {
                imageToUpload: undefined,
            },
        }));
        this.tabIndex = 0;
        this.awaitingImageUpload = false;
    }

    async onChangeIconSize(e: Event) {
        const iconSize = (e.target as RangeSliderComponent).value;

        if (this.category?._id) {
            await topicData.fetchAndUpdateCategory(
                this.category?._id.$oid,
                {
                    icon_size: iconSize
                },
                'category editor'
            );
        }
    };

    async onSetCategorySizeToDefault() {
        if (this.category?._id) {
            await topicData.fetchAndUpdateCategory(
                this.category?._id.$oid,
                {
                    icon_size: undefined
                },
                'category editor'
            );
        }
    }

    async onChangeMarkerShape(e: CustomEvent) {
        const marker_shape = undefinedIfEmpty((e.target as HTMLInputElement).value) as NumberedIconShape | undefined;

        if (this.category?._id) {
            await topicData.fetchAndUpdateCategory(
                this.category._id.$oid,
                {
                    marker_shape,
                },
                'category editor'
            );
        }
    }

    async onChangeIconUrl(e: CustomEvent) {
        const validatedIcon = await validateIcon(e.detail?.imageUrl);
        if (!validatedIcon) {
            getMainPage().errorToastElement.showToastAndThrow('The icon URL entered is invalid');
        }

        this.cancelImageUpload();

        if (this.category?._id) {
            await topicData.fetchAndUpdateCategory(
                this.category?._id.$oid,
                {
                    icon: validatedIcon
                },
                'category editor'
            );
        }
    }

    private async clickIcon(icon: SearchIcon) {
        this.pickedIcon = icon;
        this.selectedIconUrl = `https://img.icons8.com/${icon.platform}/${icon.commonName}.png`;
        await this.customIconSelected(this.selectedIconUrl);
    }

    private  async findIcon(amplitude_callsite: string, term: string) {
        if (ICONS8_KEY) {
            const response = await fetch(
                `https://search.icons8.com/api/iconsets/v5/search?term=${term}&token=${ICONS8_KEY}`,
                externalGet()
            );
            if (!response.ok) {
                await throwHttpError('An error occurred while listing icons.', response);
            }

            const icons = (await response.json()).icons as SearchIcon[];
            logWithAmplitude(amplitude_callsite, 'Icon Search', { search_text: term });
            return icons;
        } else {
            throw new Error('ICONS8_KEY can not be found');
        }
    }

    private async searchForIconInternal() {
        const search = undefinedIfEmpty(this.search.value?.trim().toLocaleLowerCase()) ?? 'map';
        this.iconSearchResults = await this.findIcon(this.amplitude_callsite, search);
    }

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    private readonly searchForIcon = debounce(this.searchForIconInternal, 200);

    private async initiateSearch(categoryName: string) {
        this.search.value = categoryName;
    }

    private setupPalette() {
        this.suggestedColors = getThemeColors();
    }

    render() {
        return html`
            <div class="edit__inputs-container">
                <div class="edit__inputs">
                    <div class="category__name_group">
                        <span class="preview__icon">
                            <img class="edit__icon-image-main" style="width:16px; height:16px; transform:${this.rotatePreviewCSS}" src="${this.renderableIconString}">
                        </span>
                        <proxi-textfield outlined autofocus id="name" class="edit__textfield flex" label="Category Name" @change=${this.onChangeCategoryName} value="${this.category?.name ?? ''}" charcounter minlength="1" maxlength="35" required></proxi-textfield>
                    </div>
                    <proxi-textarea outlined id="description" class="edit__textfield" label="Description" @change=${this.onChangeCategoryDescription} value="${this.category?.description ?? ''}" charcounter maxlength="140"></proxi-textarea>
                    <div class="category__name_group">
                        <p class="info-paragraph">
                            <b>Category Icon Size</b><br>
                            ${this.customIconSize ? html`
                                <span class="underline" @click=${async () => {this.customIconSize = false; await this.onSetCategorySizeToDefault();}}>
                                    Reset to Default
                                </span>`
                            : ''}
                        </p>
                        ${!this.customIconSize ? html`
                            <p class="info-paragraph underline" @click=${() => {this.customIconSize = true;}}>
                                Set Custom Category Icon Size
                            </p>`
                        : ''}
                        <form-tooltip
                            tipwidth="180px"
                            leftPadding="30%"
                            message="Setting a custom category icon size will override the default icon size from 'Edit Map Style', which is ${topicData.topic?.icon_size}. This override will only apply to points in this category."
                        >
                            <mwc-icon>info_outline</mwc-icon>
                        </form-tooltip>
                        ${this.customIconSize ? html`
                            <range-slider-component
                                class="flex"
                                min=${10}
                                max=${100}
                                step=${1}
                                minString="Small Icons"
                                maxString="Big Icons"
                                .value=${this.category ? this.category.icon_size ?? DEFAULT_ICON_SIZE : topicData.topic?.icon_size ?? DEFAULT_ICON_SIZE}
                                unit=${''}
                                @change=${this.onChangeIconSize}
                            ></range-slider-component>
                        ` : ''}
                    </div>
                    <div class="category__shape_group">
                        <div class="flex">
                            <p class="info-paragraph">
                                <b>Marker Shape Override</b><br>
                            </p>
                        </div>
                        <proxi-selectfield-inline
                            outlined
                            class="shape-select"
                            value="${this.category?.marker_shape ?? ''}"
                            @selected=${this.onChangeMarkerShape}
                        >
                            <mwc-list-item value="">Use Map Default (${topicData.topic?.marker_shape ?? NumberedIconShape.Circle})</mwc-list-item>
                            ${numberedIconShapeOptions.map((shape) => html`
                                <mwc-list-item value="${shape.value}">${shape.label}</mwc-list-item>
                            `)}
                        </proxi-selectfield-inline>
                        <form-tooltip
                            tipwidth="180px"
                            leftPadding="30%"
                            message="Use the map default unless this category needs a different marker shape."
                        >
                            <mwc-icon>info_outline</mwc-icon>
                        </form-tooltip>
                    </div>
                </div>

                <div class="edit__icons">
                    <span class="edit__icon ${this.tabIndex === 0 ? 'active__icon' : ''}" @click="${() => { this.tabIndex = 0; }}">
                        Select a color
                    </span>
                    <span class="edit__icon ${this.tabIndex === 1 ? 'active__icon' : ''}" @click="${async () => { this.tabIndex = 1; this.searchForIcon();  }}">
                        Icon Search
                    </span>
                    <span class="edit__icon ${this.tabIndex === 2 ? 'active__icon' : ''}" @click="${() => { this.tabIndex = 2; }}">
                        Upload Icon
                    </span>
                </div>

                ${this.tabIndex === 1 ? html`
                    <proxi-textfield
                        outlined
                        id="search"
                        class="input"
                        label="Search"
                        value=${this.category?.name ?? ''}
                        @input="${this.tabIndex === 1 ? this.searchForIcon : ''}">
                    </proxi-textfield>
                    <div class="icon-options">
                        ${this.iconSearchResults.length === 0 ? html`
                            <span class="no-results">No results found, try a different search</span>
                        `: html`
                            ${this.iconSearchResults.map(
                                (icon) => html`
                                    <mwc-button @click="${this.clickIcon.bind(this, icon)}">
                                        <img
                                            src="https://img.icons8.com/${icon.platform}/${icon.commonName}.png"
                                            width="24"
                                            height="24"/>
                                    </mwc-button>
                                `
                            )}
                        `}
                    </div>
                ` : ''}

                ${this.tabIndex === 0 ? html`
                    <div class="palette-wrapper">
                        <div class="palette-color" style="background-color: white;" @click="${this.openColorPicker}">
                            <div class="active-status">
                                <mwc-icon style="color: black">colorize</mwc-icon>
                            </div>
                        </div>
                        ${this.suggestedColors.slice(0, 15).map((paletteColor) => html`
                            <div
                                class="palette-color"
                                style="background-color: ${paletteColor};"
                                @click="${async () => { await this.onChangeCategoryIconColor(paletteColor); }}">
                                <div class="active-status">
                                    ${this.pickedColor.toLowerCase() === paletteColor.toLowerCase() ? html`
                                        <mwc-icon>check</mwc-icon>
                                    ` : ''}
                                </div>
                            </div>
                        `)}
                    </div>
                    <input id="colorPicker" type="color" @blur="${async(e: any) => { await this.pickIconColor(e.target.value); } }"/>
                ` : ''}

                ${this.tabIndex === 2 ? html`
                    <span class="no-results">For best results, upload a square image</span>
                    <image-upload-component
                        .imageUrl="${this.categoryIcon}"
                        @retreived-autoupload-url="${this.onChangeIconUrl}"
                        .autoUpload="${true}"
                        @changed-image-url=${this.onChangeIconUrl}
                    ></image-upload-component>
                    ${this.awaitingImageUpload ? html`
                        <div class="icon__actions">
                            <form-button variant="social" @click="${this.cancelImageUpload}">
                                Remove Uploaded Image
                            </form-button>
                        </div>`
                        : ''
                    }
                ` : ''}
            </div>
            <error-toast id="errorToast"></error-toast>
        `;
    }

    static get styles() {
        return css`
            .edit__inputs {
                width: 100%;
            }

            .edit__textfield {
                width: 100%;
                margin-top: 4px;
            }

            .edit__dialog {
                --mdc-dialog-min-width: 360px;
            }

            .edit__icons {
                display: flex;
                border-radius: 0.5rem;
                border: 1px solid var( --theme-input-border-color);
                overflow: hidden;
                margin-bottom: 0.5rem;
            }

            .preview__icon {
                display: flex;
                justify-content: center;
                margin-bottom: 1rem;
            }

            .edit__icon {
                flex: 1;
                display: flex;
                justify-content: center;
                padding: 0.25rem 0.5rem;
                cursor: pointer;
                background-color: white;
            }

            .edit__icon.active__icon {
                background-color: var(--theme-opacity-background-color);
            }

            .edit__icon:not(:last-child) {
                border-right: 1px solid var( --theme-input-border-color);
            }

            .edit__icon.edit__tag {
                flex-direction: column;
            }

            #colorPicker {
                opacity: 0;
                height: 0;
                width: 0;
                margin: 0;
            }

            .icon__actions {
                display: flex;
                justify-content: center;
            }

            .sub-header {
                font-size: 16px;
                margin-bottom: 0.5rem;
            }

            .underline {
                text-decoration: underline;
                cursor: pointer;
            }

            .slider-header {
                margin-bottom: 1rem;
                color: var(--theme-lighter-text-color);
            }

            .icon-options {
                display: flex;
                flex-direction: row;
                flex-wrap: wrap;
                max-height: 340px;
                overflow-y: scroll;
                margin-bottom: 1rem;
            }

            #search {
                width: 100%;
                margin: 0.5rem 0;
            }

            .add_btn {
                background-color: white;
                border: 1px solid var(--theme-lightest-color);
                border-radius: 3rem;
                padding: 0.25rem 1rem;
                margin-left: 0.25rem;
                display: flex;
                align-items: center;
                cursor: pointer;
                filter: drop-shadow(0px 4px 4px rgba(0, 0, 0, 0.25));
            }

            .palette-wrapper {
                display: flex;
                flex-wrap: wrap;
                width: 100%;
                margin: 1rem -0.25rem 0.25rem
            }

            .palette-color {
                flex: 1;
                margin: 0.25rem;
                border-radius: 50%;
                min-width: calc(12.5% - 0.5rem);
                padding-bottom: calc(12.5% - 0.5rem);
                position: relative;
                cursor: pointer;
                box-sizing: border-box;
                box-shadow: rgba(0, 0, 0, 0.3) 0px 1px 4px -1px;
            }

            .active-status {
                position: absolute;
                width: 100%;
                height: 100%;
                top: 0;
                left: 0;
                display: flex;
                justify-content: center;
                align-items: center;
                color: white;
            }

            .category__name_group {
                display: flex;
                align-items: center;
                flex-direction: row;
                gap: 1rem;
            }

            .category__shape_group {
                display: flex;
                align-items: center;
                gap: 1rem;
                margin-top: 0.75rem;
            }

            .flex {
                flex: 1;
            }

            .shape-select {
                min-width: 220px;
            }
        `;
    }

}
